import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { IColumnsMap, IFilterMap, IFiltersMap } from 'libs/design-system/src/types/data_table';
import { DataTable } from '@unified-checkout/design-system';
import { Pen } from '@unified-checkout/design-system/icons/index';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useLangStore } from '@app/dashboard/store/lang';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { getTerminals } from '@app/dashboard/services/inner_layout/terminals';
import { getBranches } from '@app/dashboard/services/inner_layout/branches';
import { getCountryFromDomain } from '@app/dashboard/utils/getCountryFromDomain';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { UpdateTerminalBranchModal } from './UpdateTerminalBranchModal';
import type { SelectedTerminal, TerminalRow } from '@app/dashboard/types/innerLayout/terminals';
import styles from './devices.module.scss';

const TABLE_ID = 'devices_table';

export const Devices = () => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const canUpdateTerminal = isAccessible(PERMISSIONS.CAN_UPDATE_TERMINAL);
  const [selectedTerminal, setSelectedTerminal] = useState<SelectedTerminal | null>(null);

  const handleOpenUpdateModal = (row: TerminalRow) => {
    setSelectedTerminal({
      id: row.id,
      branch_id: row.branch?.branch_id,
    });
  };

  const handleCloseUpdateModal = () => {
    setSelectedTerminal(null);
  };

  const columnsMap: IColumnsMap = [
    {
      id: 'id',
      name: t('innerLayout.devicesPage.table.headers.id'),
      type: 'text',
      key: 'id',
    },
    {
      id: 'created_at',
      name: t('innerLayout.devicesPage.table.headers.createdAt'),
      type: 'date',
      key: 'created_at',
      hintText: 'created_at',
    },
    {
      id: 'branch_id',
      name: t('innerLayout.devicesPage.table.headers.branchId'),
      type: 'text',
      key: 'branch',
      format: (_fullData: unknown, value: { branch_id?: string, branch_name?: string, address?: string, city?: string } | null) =>
        value != null && typeof value === 'object' && 'branch_id' in value ? String(value.branch_id) : '-',
    },
    ...(canUpdateTerminal ? [
      {
        id: 'actions',
        name: t('innerLayout.devicesPage.table.headers.actions'),
        type: 'action_btns' as const,
        key: 'id',
        actionBtnsProps: [
          {
            leadingIcon: Pen,
            text: t('innerLayout.update'),
            onClickHandler: handleOpenUpdateModal,
            variant: 'link' as const,
            color: 'primary' as const,
          },
        ],
      },
    ] : []),
  ];

  const filtersResponse = [
    {
      filter_name: t('innerLayout.devicesPage.table.filters.branchId'),
      filter_key: 'branch_id',
      filter_type: 'text_box',
      filter_place_holder: '1234',
      filter_validation: [
        {
          regex: '^\\d{1,15}$',
          message: t('innerLayout.devicesPage.table.filters.validation.branchIdMessage'),
        },
      ],
    },
  ];

  const filtersMap: IFiltersMap = useMemo(
    () =>
      filtersResponse.map((filter: Record<string, unknown>) => ({
        type: 'text' as IFilterMap['type'],
        key: filter.filter_key as string,
        name: filter.filter_name as string,
        values: [],
        placeHolder: filter.filter_place_holder as string,
        validations: filter.filter_validation as IFilterMap['validations'],
      })),
    [lang]
  );

  const queryKeys = useMemo(() => [QUERY_KEYS.GET_TERMINALS, isLive], [isLive]);
  const queryFunctionParams = useMemo(() => ({ is_live: isLive }), [isLive]);
  const resetFiltersParams = useMemo(() => [isLive], [isLive]);

  // Fetch branches for update device branch dropdown
  const { data: branchesData, isLoading: isLoadingBranches } = useQuery({
    queryKey: [QUERY_KEYS.GET_BRANCHES, isLive, 1, 1000],
    queryFn: () => getBranches({ currentPage: 1, pageSize: 1000, params: { is_live: isLive } }),
  });

  const branches = branchesData?.data?.results || [];

  return (
    <div className={styles['devices-wrapper']}>
      <div className={styles['header']}>
        <div className={styles['header__title']}>
          {t('innerLayout.devicesPage.title')}
        </div>
      </div>

      <DataTable
        lang={lang}
        filters={{
          filtersMap,
          isMobile,
          country: getCountryFromDomain(),
        }}
        tableBody={{
          lang,
          tableId: TABLE_ID,
          dataLocation: 'data.results',
          columnsMap,
        }}
        pagination={{
          hideLabels: isMobile,
          type: 'cursor_based',
          dataLocation: {
            next: 'data.next',
            prev: 'data.previous',
          },
          isMobile,
        }}
        queries={{
          queryKeys,
          queryFunction: getTerminals,
          queryFunctionParams,
          resetFiltersParams,
        }}
        emptyState={{
          text: t('innerLayout.devicesPage.table.body.noDevicesYet'),
          description: t('innerLayout.devicesPage.table.body.noDevicesHint'),
        }}
      />

      <UpdateTerminalBranchModal
        open={!!selectedTerminal?.id}
        onClose={handleCloseUpdateModal}
        terminalId={selectedTerminal?.id as string}
        terminalBranchId={selectedTerminal?.branch_id}
        isMobile={isMobile}
        branches={branches}
        isLoadingBranches={isLoadingBranches}
      />
    </div>
  );
};
